import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDeleteQuote, useMe, useQuote, useRefreshQuote, useRef, useSubmitQuote, useUpdateQuoteLines } from '../api/hooks';
import { StatusBadge } from '../components/StatusBadge';
import { fmtDate, fmtMoney } from '../lib/format';

interface EditRow {
  id?: string;
  isNew: boolean;
  itemName: string;
  supplierName: string;
  qty: string;
  unitPrice: string;
  unitCost: string;
}
const emptyEditRow = (): EditRow => ({ isNew: true, itemName: '', supplierName: '', qty: '1', unitPrice: '', unitCost: '' });

export function QuoteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const quote = useQuote(id);
  const me = useMe();
  const refresh = useRefreshQuote();
  const submit = useSubmitQuote();
  const del = useDeleteQuote();
  const update = useUpdateQuoteLines();
  const items = useRef('items');
  const vendors = useRef('vendors');
  const isApprover = me.data?.user?.role === 'approver';

  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState<EditRow[]>([]);

  if (quote.isLoading) return <p className="muted">Loading…</p>;
  if (quote.error) return <p className="error-text">{(quote.error as Error).message}</p>;
  const q = quote.data;
  if (!q) return null;

  const startEdit = () => {
    setRows(
      (q.lineItems ?? []).map((l) => ({
        id: l.id,
        isNew: false,
        itemName: l.itemName ?? '—',
        supplierName: l.supplierName ?? '',
        qty: l.qty != null ? String(l.qty) : '1',
        unitPrice: l.unitPrice != null ? String(l.unitPrice) : '',
        unitCost: l.unitCost != null ? String(l.unitCost) : '',
      })),
    );
    setEditing(true);
  };

  const setRow = (i: number, patch: Partial<EditRow>) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i: number) => setRows((rs) => (rs.length > 1 ? rs.filter((_, idx) => idx !== i) : rs));
  const addRow = () => setRows((rs) => [...rs, emptyEditRow()]);

  const amtOf = (r: EditRow) => (Number(r.qty) || 0) * (Number(r.unitPrice) || 0);
  const costOf = (r: EditRow) => (Number(r.qty) || 0) * (Number(r.unitCost) || 0);
  const eSubtotal = rows.reduce((s, r) => s + amtOf(r), 0);
  const eCost = rows.reduce((s, r) => s + costOf(r), 0);
  const editValid =
    rows.length > 0 && rows.every((r) => (!r.isNew || r.itemName.trim()) && Number(r.qty) > 0 && r.unitPrice !== '' && r.unitCost !== '');

  const saveEdit = () => {
    update.mutate(
      {
        id: q.id,
        lines: rows.map((r) =>
          r.isNew
            ? {
                itemName: r.itemName.trim(),
                supplierName: r.supplierName.trim() || undefined,
                qty: Number(r.qty),
                unitPrice: Number(r.unitPrice),
                unitCost: Number(r.unitCost),
              }
            : { id: r.id!, qty: Number(r.qty), unitPrice: Number(r.unitPrice), unitCost: Number(r.unitCost) },
        ),
      },
      { onSuccess: () => setEditing(false) },
    );
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{q.quoteNumber}</h1>
          <span className="muted">
            {q.customerName}
            {q.creatorFirstName ? ` · by ${q.creatorFirstName}` : ''}
          </span>
        </div>
        <div className="btn-row">
          <StatusBadge status={q.status} />
          {!editing && q.status === 'Created' && <button onClick={startEdit}>Edit lines</button>}
          {isApprover && q.status === 'Created' && !editing && (
            <button className="primary" onClick={() => submit.mutate(q.id)} disabled={submit.isPending}>
              {submit.isPending ? 'Submitting…' : 'Submit to QuickBooks'}
            </button>
          )}
          {q.qboEstimateId && !editing && (
            <button onClick={() => refresh.mutate(q.id)} disabled={refresh.isPending}>
              {refresh.isPending ? 'Refreshing…' : 'Refresh status'}
            </button>
          )}
          {q.status !== 'Closed' && !editing && (
            <button
              className="link"
              onClick={() => {
                if (window.confirm(`Delete ${q.quoteNumber}?`)) del.mutate(q.id, { onSuccess: () => navigate('/') });
              }}
              disabled={del.isPending}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <div className="row cols-3">
          <div>
            <label>QBO Estimate</label>
            {q.qboEstimateId ?? '—'}
            {q.qboDocNumber ? ` (${q.qboDocNumber})` : ''}
          </div>
          <div>
            <label>Customer email</label>
            {q.customerEmail ?? '—'}
          </div>
          <div>
            <label>Expires</label>
            {fmtDate(q.expirationDate)}
          </div>
        </div>
        {q.invoiceQboId && (
          <div className="banner ok" style={{ marginTop: '1rem' }}>
            Converted to Invoice {q.invoiceQboId} on {fmtDate(q.invoicedAt)}.
          </div>
        )}
      </div>

      <div className="card">
        <h2>Line items</h2>
        {update.error && <div className="banner error">{(update.error as Error).message}</div>}

        {!editing ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Supplier</th>
                  <th className="right">Qty</th>
                  <th className="right">Unit</th>
                  <th className="right">Cost</th>
                  <th className="right">Amount</th>
                  <th className="right">Margin</th>
                </tr>
              </thead>
              <tbody>
                {(q.lineItems ?? []).map((l) => (
                  <tr key={l.id}>
                    <td>{l.itemName ?? '—'}</td>
                    <td>{l.supplierName ?? '—'}</td>
                    <td className="right">{l.qty ?? ''}</td>
                    <td className="right">{fmtMoney(l.unitPrice, q.currency)}</td>
                    <td className="right">{fmtMoney(l.unitCost, q.currency)}</td>
                    <td className="right">{fmtMoney(l.amount, q.currency)}</td>
                    <td className="right" style={{ color: (l.margin ?? 0) >= 0 ? 'var(--status-invoiced)' : 'var(--status-rejected)' }}>
                      {fmtMoney(l.margin, q.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="right">Qty</th>
                    <th className="right">Unit price</th>
                    <th className="right">Cost / unit</th>
                    <th className="right">Amount</th>
                    <th className="right">Margin</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const margin = amtOf(r) - costOf(r);
                    return (
                      <tr key={r.id ?? `new-${i}`}>
                        <td>
                          {r.isNew ? (
                            <>
                              <input list="edit-item-list" value={r.itemName} onChange={(e) => setRow(i, { itemName: e.target.value })} placeholder="Item / service *" />
                              <input list="edit-vendor-list" value={r.supplierName} onChange={(e) => setRow(i, { supplierName: e.target.value })} placeholder="Supplier (optional)" style={{ marginTop: 4 }} />
                            </>
                          ) : (
                            <>
                              {r.itemName}
                              {r.supplierName ? <span className="muted"> · {r.supplierName}</span> : ''}
                            </>
                          )}
                        </td>
                        <td className="right">
                          <input type="number" min="0" step="1" value={r.qty} onChange={(e) => setRow(i, { qty: e.target.value })} style={{ maxWidth: 90 }} />
                        </td>
                        <td className="right">
                          <input type="number" min="0" step="0.01" value={r.unitPrice} onChange={(e) => setRow(i, { unitPrice: e.target.value })} style={{ maxWidth: 110 }} />
                        </td>
                        <td className="right">
                          <input type="number" min="0" step="0.01" value={r.unitCost} onChange={(e) => setRow(i, { unitCost: e.target.value })} style={{ maxWidth: 110 }} />
                        </td>
                        <td className="right">{fmtMoney(amtOf(r), q.currency)}</td>
                        <td className="right" style={{ color: margin >= 0 ? 'var(--status-invoiced)' : 'var(--status-rejected)' }}>
                          {fmtMoney(margin, q.currency)}
                        </td>
                        <td className="right">
                          <button type="button" className="link" onClick={() => removeRow(i)} disabled={rows.length === 1}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <datalist id="edit-item-list">{items.data?.map((it) => <option key={it.id} value={it.name} />)}</datalist>
            <datalist id="edit-vendor-list">{vendors.data?.map((v) => <option key={v.id} value={v.name} />)}</datalist>
            <div style={{ marginTop: '0.75rem' }}>
              <button type="button" onClick={addRow}>+ Add line</button>
            </div>
            <div className="btn-row" style={{ marginTop: '1rem' }}>
              <button className="primary" onClick={saveEdit} disabled={!editValid || update.isPending}>
                {update.isPending ? 'Saving…' : 'Save changes'}
              </button>
              <button className="link" onClick={() => setEditing(false)} disabled={update.isPending}>
                Cancel
              </button>
              <span className="muted">
                Add or remove lines and change qty, selling price and cost. New items are created in QuickBooks on save; tax is applied when the quote is submitted.
              </span>
            </div>
          </>
        )}

        <div className="right" style={{ marginTop: '0.75rem', lineHeight: 1.8 }}>
          {editing ? (
            <>
              <div className="muted">Total cost {fmtMoney(eCost, q.currency)}</div>
              <div>
                <strong>Total {fmtMoney(eSubtotal, q.currency)}</strong> ·{' '}
                <span style={{ color: eSubtotal - eCost >= 0 ? 'var(--status-invoiced)' : 'var(--status-rejected)' }}>
                  Margin {fmtMoney(eSubtotal - eCost, q.currency)}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="muted">
                Subtotal {fmtMoney(q.subtotal, q.currency)} · Tax {fmtMoney(q.taxTotal, q.currency)} · Total cost {fmtMoney(q.totalCost, q.currency)}
              </div>
              <div>
                <strong>Total {fmtMoney(q.totalAmount, q.currency)}</strong> ·{' '}
                <span style={{ color: q.totalMargin >= 0 ? 'var(--status-invoiced)' : 'var(--status-rejected)' }}>
                  Margin {fmtMoney(q.totalMargin, q.currency)}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      <Link to="/">← Back to quotes</Link>
    </>
  );
}
